import { HelpCircle } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import type { DashboardRevenueConfigResponse } from '@/api/apiService';

export type SiteConfig = DashboardRevenueConfigResponse['config'];

/**
 * Plan tier = billing/entitlement lever (stored as revenue_config.plan_tier).
 * Only platform admins may change plan_tier (API also enforces this).
 *
 * Growth remains a valid backend tier (existing sites) but is hidden from the
 * picker — admins choose Starter or Pro. Growth still appears if the site is
 * already on it so the select value stays valid.
 */
export const PLAN_TIERS = [
  { value: 'starter', label: 'Starter — chat + human handoff; booking/CRM locked', selectable: true },
  { value: 'growth', label: 'Growth — legacy (kept for existing sites)', selectable: false },
  { value: 'pro', label: 'Pro — booking, leads, CRM; most proactive', selectable: true },
];

/** Tiers shown in the admin plan picker (Growth only when already selected). */
export function visiblePlanTiers(currentTier: string) {
  return PLAN_TIERS.filter((tier) => tier.selectable || tier.value === currentTier);
}

export const CRM_PROVIDERS = [
  { value: 'none', label: 'None — do not sync to a CRM' },
  { value: 'hubspot', label: 'HubSpot — REST API (access token)' },
  { value: 'webhook', label: 'Other CRM — HTTP webhook (Salesforce, Pipedrive, Zapier, custom)' },
] as const;

export type CrmProvider = (typeof CRM_PROVIDERS)[number]['value'];

export const WEEK_DAYS = [
  { value: 0, label: 'Mon' },
  { value: 1, label: 'Tue' },
  { value: 2, label: 'Wed' },
  { value: 3, label: 'Thu' },
  { value: 4, label: 'Fri' },
  { value: 5, label: 'Sat' },
  { value: 6, label: 'Sun' },
];

const MAIN_TIMEZONES: { tz: string; city: string; country: string }[] = [
  { tz: 'Pacific/Midway', city: 'Midway', country: 'USA' },
  { tz: 'Pacific/Honolulu', city: 'Honolulu', country: 'USA' },
  { tz: 'America/Anchorage', city: 'Anchorage', country: 'USA' },
  { tz: 'America/Los_Angeles', city: 'Los Angeles', country: 'USA' },
  { tz: 'America/Denver', city: 'Denver', country: 'USA' },
  { tz: 'America/Chicago', city: 'Chicago', country: 'USA' },
  { tz: 'America/New_York', city: 'New York', country: 'USA' },
  { tz: 'America/Sao_Paulo', city: 'Sao Paulo', country: 'Brazil' },
  { tz: 'UTC', city: 'UTC', country: '' },
  { tz: 'Europe/London', city: 'London', country: 'UK' },
  { tz: 'Europe/Paris', city: 'Paris', country: 'France' },
  { tz: 'Europe/Sofia', city: 'Sofia', country: 'Bulgaria' },
  { tz: 'Europe/Moscow', city: 'Moscow', country: 'Russia' },
  { tz: 'Africa/Nairobi', city: 'Nairobi', country: 'Kenya' },
  { tz: 'Asia/Dubai', city: 'Dubai', country: 'UAE' },
  { tz: 'Asia/Karachi', city: 'Karachi', country: 'Pakistan' },
  { tz: 'Asia/Kolkata', city: 'Kolkata', country: 'India' },
  { tz: 'Asia/Dhaka', city: 'Dhaka', country: 'Bangladesh' },
  { tz: 'Asia/Bangkok', city: 'Bangkok', country: 'Thailand' },
  { tz: 'Asia/Shanghai', city: 'Shanghai', country: 'China' },
  { tz: 'Asia/Tokyo', city: 'Tokyo', country: 'Japan' },
  { tz: 'Australia/Sydney', city: 'Sydney', country: 'Australia' },
  { tz: 'Pacific/Auckland', city: 'Auckland', country: 'New Zealand' },
];

export const SITE_LANGUAGE_OPTIONS: { code: string; label: string }[] = [
  { code: 'en', label: 'English' },
  { code: 'bg', label: 'Bulgarian' },
  { code: 'de', label: 'German' },
  { code: 'fr', label: 'French' },
  { code: 'es', label: 'Spanish' },
  { code: 'it', label: 'Italian' },
  { code: 'pt', label: 'Portuguese' },
  { code: 'nl', label: 'Dutch' },
  { code: 'pl', label: 'Polish' },
  { code: 'ro', label: 'Romanian' },
  { code: 'ru', label: 'Russian' },
  { code: 'uk', label: 'Ukrainian' },
  { code: 'tr', label: 'Turkish' },
  { code: 'el', label: 'Greek' },
  { code: 'ar', label: 'Arabic' },
  { code: 'zh', label: 'Chinese' },
  { code: 'ja', label: 'Japanese' },
];

export const getUtcOffsetMinutes = (timeZone: string, date: Date = new Date()): number => {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
  const parts = dtf.formatToParts(date).reduce<Record<string, string>>((acc, part) => {
    acc[part.type] = part.value;
    return acc;
  }, {});
  const asUtc = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(parts.hour) % 24,
    Number(parts.minute),
    Number(parts.second)
  );
  return Math.round((asUtc - date.getTime()) / 60000);
};

export const formatUtcOffset = (minutes: number): string => {
  const sign = minutes >= 0 ? '+' : '-';
  const abs = Math.abs(minutes);
  const hours = Math.floor(abs / 60);
  const mins = abs % 60;
  return `UTC${sign}${hours}${mins ? `:${String(mins).padStart(2, '0')}` : ''}`;
};

export const TIMEZONE_OPTIONS: { value: string; label: string }[] = MAIN_TIMEZONES.map(({ tz, city, country }) => {
  if (tz === 'UTC') return { value: tz, label: 'UTC' };
  let offsetLabel = '';
  try {
    offsetLabel = formatUtcOffset(getUtcOffsetMinutes(tz));
  } catch {
    offsetLabel = '';
  }
  const place = country ? `${city}, ${country}` : city;
  return { value: tz, label: offsetLabel ? `${place} (${offsetLabel})` : place };
}).sort((a, b) => getUtcOffsetMinutes(a.value) - getUtcOffsetMinutes(b.value));

export const todayLocalIso = () => {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};

export interface DayScheduleEntry {
  enabled: boolean;
  start: string;
  end: string;
}

export type DaySchedule = Record<number, DayScheduleEntry>;

export const DEFAULT_DAY_SCHEDULE = (): DaySchedule => ({
  0: { enabled: true, start: '09:00', end: '17:00' },
  1: { enabled: true, start: '09:00', end: '17:00' },
  2: { enabled: true, start: '09:00', end: '17:00' },
  3: { enabled: true, start: '09:00', end: '17:00' },
  4: { enabled: true, start: '09:00', end: '17:00' },
  5: { enabled: false, start: '09:00', end: '17:00' },
  6: { enabled: false, start: '09:00', end: '17:00' },
});

export interface DateOverrideRow {
  date: string;
  enabled: boolean;
  start: string;
  end: string;
  reason: string;
}

export const EDITABLE_ROLES = ['owner', 'editor', 'platform_admin'];

export interface AssigneeRow {
  id?: string;
  name: string;
}

export interface ServiceRow {
  name: string;
  duration_minutes: string;
  booking_url: string;
}

export interface ServicePriceRow {
  service: string;
  assignee: string;
  amount: string;
}

export const PRICE_CURRENCIES = ['EUR', 'USD', 'GBP'] as const;

export interface BreakRow {
  start: string;
  end: string;
  days: number[];
}

export interface UnavailableWindowRow {
  resource_key: string;
  start_at_local: string;
  end_at_local: string;
  reason: string;
}

export const sanitizeKey = (value: string, fallback: string) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || fallback;

export const asLocalInputValue = (iso: string) => {
  if (!iso) return '';
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
};

export const toUtcIso = (localValue: string) => {
  const dt = new Date(localValue);
  if (Number.isNaN(dt.getTime())) return '';
  return dt.toISOString();
};

export const FieldLabel = ({ text, hint }: { text: string; hint: string }) => (
  <div className="flex items-center gap-1.5">
    <Label>{text}</Label>
    <Tooltip>
      <TooltipTrigger asChild>
        <button type="button" className="cursor-help text-muted-foreground hover:text-foreground">
          <HelpCircle className="h-3.5 w-3.5" />
        </button>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs text-xs leading-relaxed">
        {hint}
      </TooltipContent>
    </Tooltip>
  </div>
);

export const prettyJson = (value: unknown) => JSON.stringify(value ?? {}, null, 2);

export const DASHBOARD_CONTACT_ADMIN = {
  to: '/dashboard/contact',
  end: false,
  label: 'Contact admin',
} as const;

export const DASHBOARD_NAV = [
  { to: '/dashboard', end: true, label: 'Overview' },
  { to: '/dashboard/content', end: false, label: 'Content' },
  { to: '/dashboard/widget', end: false, label: 'Widget' },
  { to: '/dashboard/billing', end: false, label: 'Billing' },
  { to: '/dashboard/bookings', end: false, label: 'Bookings' },
  { to: '/dashboard/schedule', end: false, label: 'Schedule' },
  { to: '/dashboard/catalog', end: false, label: 'Catalog' },
  { to: '/dashboard/automation', end: false, label: 'Automation' },
  { to: '/dashboard/help', end: false, label: 'Help' },
  { to: '/dashboard/advanced', end: false, label: 'Advanced', adminOnly: true },
] as const;
